import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';

import { StaticItemsCustomSelect } from '@/components/molecules/StaticItemsCustomSelect';
import { LocaleContext, useTranslator } from '@/hooks/useTranslator';
import { DateObject } from '@/types/date';
import { SelectItem } from '@/types/fields';
import { Locale } from '@/types/locale';
import { allowOnlyNumbers } from '@/utility/allowOnlyNumbers';
import { NumericRange } from '@/utility/numericRange';

interface Props {
  readonly name?: string;
  readonly defaultValue?: unknown;
}

export const DateFormFieldElement: React.FC<Props> = (props: Props): React.ReactElement => {
  const locale = React.useContext<Locale>(LocaleContext);
  const tr = useTranslator();

  const { defaultValue, name } = props;

  const formatter = React.useMemo(
    (): Intl.DateTimeFormat => new Intl.DateTimeFormat(locale, { month: 'long' }),
    [locale],
  );

  const [dateObject, setDateObject] = React.useState<DateObject>(
    DateObject.fromString(defaultValue),
  );

  const months = React.useMemo((): SelectItem<number>[] => createMonths(formatter), [formatter]);

  const updateValue = React.useCallback((change: Partial<DateObject>): void => {
    setDateObject((previousDateObject: DateObject): DateObject => {
      if (!DateObject.willChange(previousDateObject, change)) {
        return previousDateObject;
      }

      return DateObject.copyWith(previousDateObject, change);
    });
  }, []);

  const handleYearChange = React.useCallback(
    (event: React.FocusEvent<HTMLInputElement>): void => {
      const { value } = event.target;
      const year = Number(value);
      if (isNaN(year)) {
        return;
      }
      updateValue({ year: year });
    },
    [updateValue],
  );

  const handleMonthChange = React.useCallback(
    (month: number): void => {
      if (month < 1 || month > 12) {
        return;
      }
      updateValue({ month: month });
    },
    [updateValue],
  );

  const handleDayChange = React.useCallback(
    (day: number): void => {
      if (day < 1 || day > 31) {
        return;
      }
      updateValue({ day: day });
    },
    [updateValue],
  );

  const value = React.useMemo((): string => DateObject.toISOString(dateObject), [dateObject]);

  return (
    <Box sx={styles.container}>
      <input name={name} type="hidden" value={value} readOnly={true} />

      <Box flex={1}>
        <StaticItemsCustomSelect
          label={tr('DAY')}
          items={days}
          value={dateObject.day}
          onChange={handleDayChange}
        />
      </Box>
      <Box flex={1}>
        <StaticItemsCustomSelect
          label={tr('MONTH')}
          items={months}
          value={dateObject.month}
          onChange={handleMonthChange}
        />
      </Box>
      <Box flex={1}>
        <TextField
          label={tr('YEAR')}
          fullWidth={true}
          defaultValue={dateObject.year}
          onKeyDown={allowOnlyNumbers}
          onBlur={handleYearChange}
        />
      </Box>
    </Box>
  );
};

const styles: Record<string, SystemStyleObject> = {
  container: {
    display: 'flex',
    gap: 2,
  },
};

const days = NumericRange.from(1)
  .to(31)
  .map(
    (day: number): SelectItem<number> => ({
      label: String(day),
      value: day,
    }),
  );

const createMonths = (formatter: Intl.DateTimeFormat): SelectItem<number>[] =>
  NumericRange.from(1)
    .to(12)
    .map(
      (month: number): SelectItem<number> => ({
        value: month,
        label: formatter.format(new Date(0, month - 1, 1)),
      }),
    );
